import { lazy, Suspense } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ProtectedRoute } from '@/core/router/ProtectedRoute';
import { DashboardRouter } from '@/core/router/DashboardRouter';
import { AppLayout } from '@/core/layout';
import {
  PreLoginShell,
  PreLoginLanding,
  GuestSignupModalProvider,
  PreLoginGuestActionGateProvider,
  AccountCreationModal,
  UnauthAttributionProvider,
} from '@/features/pre-login';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { TakeAssessmentPage } from '@/features/longevity-assessment';
import { useGuestSignupModal } from '@/features/pre-login/context/GuestSignupModalContext';

// T232: Lazy load resource hub components for code splitting
// This reduces initial bundle size and improves performance
const ResourceLibrary = lazy(() =>
  import('@/features/resource-hub/components/ResourceLibrary').then((module) => ({
    default: module.ResourceLibrary,
  })),
);

const ResourceDetail = lazy(() =>
  import('@/features/resource-hub/components/ResourceDetail').then((module) => ({
    default: module.ResourceDetail,
  })),
);

export const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
      <p className="mt-2 text-sm text-gray-600">Loading...</p>
    </div>
  </div>
);

// Feature 102: public referral-feedback page (email token link). Lazy — only
// email recipients ever hit it.
const FeedbackPage = lazy(() =>
  import('@/features/email-feedback/FeedbackPage').then((module) => ({
    default: module.FeedbackPage,
  })),
);

export const FeedbackRoute = () => (
  <ErrorBoundary>
    <Suspense fallback={<LoadingFallback />}>
      <FeedbackPage />
    </Suspense>
  </ErrorBoundary>
);

export const ResourceLibraryRoute = () => (
  <ErrorBoundary>
    <Suspense fallback={<LoadingFallback />}>
      <ResourceLibrary />
    </Suspense>
  </ErrorBoundary>
);

export const ResourceDetailRoute = () => {
  const { seoUrl } = useParams<{ seoUrl: string }>();
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <ResourceDetail seoUrl={seoUrl || ''} />
      </Suspense>
    </ErrorBoundary>
  );
};

// Redirect legacy /partner-sign-up to /partner-signup preserving query params (e.g. referralCode)
export const LegacyPartnerSignupRedirect = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.toString();
  return <Navigate to={`/partner-signup${query ? `?${query}` : ''}`} replace />;
};

// Spec Item 1: /signup and variants redirect to / preserving referral code or invite token.
// The new care-seeker signup flow lives in the account-creation modal (Item 3).
export const SignupToLandingRedirect = () => {
  const [searchParams] = useSearchParams();
  const { inviteId } = useParams<{ inviteId?: string }>();
  const query = new URLSearchParams(searchParams.toString());
  if (inviteId) query.set('inviteId', inviteId);
  const qs = query.toString();
  return <Navigate to={`/${qs ? `?${qs}` : ''}`} replace />;
};

// Spec Item 1: /partner-signup redirects to / preserving referral code.
export const PartnerSignupToLandingRedirect = () => {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={`/${qs ? `?${qs}` : ''}`} replace />;
};

export const RoleAwareDashboardRoute = () => (
  <ProtectedRoute>
    <DashboardRouter />
  </ProtectedRoute>
);

// Renders AppLayout for authenticated users, PreLoginShell for unauthenticated users.
// Used for routes that are publicly accessible but need different layouts per auth state.
// Guests get:
//   - `UnauthAttributionProvider` — single resolver of referral/invite URL params
//     (spec Item 5 & 6). Mounting it here (one ancestor for every consumer) ensures
//     URL params are parsed once and session_start RUM events fire exactly once.
//   - `GuestSignupModalProvider` — gated actions across the shell pop the signup
//     modal (spec Item 2).
export const AuthAwareLayout = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingFallback />;
  if (isAuthenticated) return <AppLayout />;
  return (
    <UnauthAttributionProvider>
      <GuestSignupModalProvider>
        <PreLoginGuestActionGateProvider>
          <PreLoginShell />
          <AccountCreationModal />
        </PreLoginGuestActionGateProvider>
      </GuestSignupModalProvider>
    </UnauthAttributionProvider>
  );
};

// Navigator chatbot layout. Authenticated users keep the standard AppLayout.
// Guests get the same PreLoginShell as the other public pages (guest sidebar +
// "Create an account" banner), wrapped in the guest providers so account-only
// actions (book consultation, "my care plans") can pop the signup modal.
// Backend scopes guest navigator data by the session UUID.
export const ChatbotAuthAwareLayout = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingFallback />;
  if (isAuthenticated) return <AppLayout />;
  return (
    <UnauthAttributionProvider>
      <GuestSignupModalProvider>
        <PreLoginGuestActionGateProvider>
          <PreLoginShell />
          <AccountCreationModal />
        </PreLoginGuestActionGateProvider>
      </GuestSignupModalProvider>
    </UnauthAttributionProvider>
  );
};

// Feature 095: public longevity-assessment take flow. Wrapped in the guest
// providers so the completion screen can pop the signup modal (email pre-filled,
// advisor step skipped); the assessment is claimed to the new account afterward.
// The pre-login coupling lives here (the composition layer), not in the longevity
// feature — TakeAssessmentPage just receives an onCreateAccount callback.
const LongevityTakeInner = () => {
  const { openModal } = useGuestSignupModal();
  const { login } = useAuth();
  return (
    <TakeAssessmentPage
      onCreateAccount={(email) =>
        openModal({ trigger: 'action_button_click', email, skipAdvisorStep: true })
      }
      onSignIn={login}
    />
  );
};

export const LongevityTakeRoute = () => (
  <UnauthAttributionProvider>
    <GuestSignupModalProvider>
      <PreLoginGuestActionGateProvider>
        <LongevityTakeInner />
        <AccountCreationModal />
      </PreLoginGuestActionGateProvider>
    </GuestSignupModalProvider>
  </UnauthAttributionProvider>
);

// Redirects authenticated users to their dashboard; shows landing page for guests.
export const PreLoginLandingOrRedirect = () => {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) return <Navigate to="/my" replace />;
  return <PreLoginLanding />;
};
